import { useEffect, useState } from "react";
import type { PlatformClient } from "@hololive/browser-sdk/platform";
import type { PresenceProjection } from "@hololive/browser-sdk";
import { useUi } from "./ui-messages";
import { InlineFeedback } from "./feedback";
import { formatError } from "./api";

export const presenceDelay = (failures = 0) => failures ? Math.min(120_000, 2000 * 2 ** Math.min(failures - 1, 6)) * (0.8 + Math.random() * 0.4) : (document.hidden ? 120_000 : 45_000);

// One serial lease loop; visibility changes reconcile immediately and never
// create a second request while a renewal is still pending.
export function startPresenceLease(send: (signal: AbortSignal) => Promise<unknown>, changed: (status: "online"|"unknown", error: string) => void) {
  const abort = new AbortController(); let timer: ReturnType<typeof setTimeout>; let pending = false; let failures = 0;
  const tick = async () => {
    if (abort.signal.aborted || pending) return;
    clearTimeout(timer); pending = true;
    try { await send(abort.signal); if (!abort.signal.aborted) { failures = 0; changed("online", ""); } }
    catch (error) { if (!abort.signal.aborted) { failures++; changed("unknown", formatError(error)); } }
    finally { pending = false; if (!abort.signal.aborted) timer = setTimeout(tick, presenceDelay(failures)); }
  };
  const visible = () => { if (!document.hidden) void tick(); };
  document.addEventListener("visibilitychange", visible); void tick();
  return () => { abort.abort(); clearTimeout(timer); document.removeEventListener("visibilitychange", visible); };
}

export function useAppPresence(client: PlatformClient | null) {
  const [status, setStatus] = useState<"online"|"unknown">("unknown");
  const [error, setError] = useState("");
  useEffect(() => {
    setStatus("unknown"); setError("");
    if (!client) return;
    return startPresenceLease(signal => client.heartbeatAppPresence(`presence-${crypto.randomUUID()}`, signal), (state, message) => { setStatus(state); setError(message); });
  }, [client]);
  return { status, error };
}

export function PresenceBadge({ value }: { value?: PresenceProjection }) {
  const ui = useUi(); const status = value?.status || "unknown";
  return <span className={`presence-badge presence-${status}`} title={value?.last_seen_at ? `${ui("Last heartbeat")}: ${new Date(value.last_seen_at).toLocaleString()}` : undefined}>{ui(status === "online" ? "presenceOnline" : status === "offline" ? "presenceOffline" : "presenceUnknown")}</span>;
}

export function AppPresenceStatus({ value }: { value: ReturnType<typeof useAppPresence> }) {
  const ui = useUi();
  return <div className="app-presence" aria-label={ui("My presence")}><PresenceBadge value={{status:value.status,observed_at:new Date().toISOString()}} /><InlineFeedback value={value.error ? {text:`${ui("Presence temporarily unavailable")} · ${value.error}`,level:"warning"} : undefined} /></div>;
}

// A single visible detail subscribes to one bounded SSE. Always reload the
// authoritative projection after an edge, disconnect or idle expiry.
export function watchPresence(client: PlatformClient, kind: "user"|"endpoint", id: string, refresh: () => Promise<void>) {
  const abort = new AbortController(); let timer: ReturnType<typeof setTimeout>; let failures = 0;
  const run = async () => {
    if (abort.signal.aborted) return;
    if (document.hidden) { timer = setTimeout(run, 15_000); return; }
    try {
      await refresh(); if (abort.signal.aborted) return;
      const stream = await client.openPresenceStream(kind, id, abort.signal);
      const reader = stream.getReader();
      try { while (!abort.signal.aborted && !(await reader.read()).done) { /* drain snapshot/edge; re-read authoritative data on close */ } }
      finally { await reader.cancel().catch(() => undefined); reader.releaseLock(); }
      failures = 0;
    } catch { failures++; }
    if (!abort.signal.aborted) timer = setTimeout(run, failures ? presenceDelay(failures) : 750 + Math.random() * 1250);
  };
  void run();
  return () => { abort.abort(); clearTimeout(timer); };
}
